import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';
import Overlay from 'react-overlays/Overlay';

import GifBoxIcon from '@/material-icons/400-24px/gif_box.svg?react';
import { IconButton } from 'mastodon/components/icon_button';
import { searchGifs, attachGif } from 'mastodon/actions/gifs';
import { useAppDispatch } from 'mastodon/store';

const messages = defineMessages({
  open: { id: 'gif_picker.open', defaultMessage: 'Insert GIF' },
  search: { id: 'gif_picker.search', defaultMessage: 'Search GIFs' },
  empty: { id: 'gif_picker.empty', defaultMessage: 'No GIFs found' },
});

interface GifResult {
  id: string;
  description: string;
  preview: { url: string; width: number; height: number };
  gif: { url: string; width: number; height: number };
}

interface SearchResponse {
  items: GifResult[];
  next_offset: number | null;
}

const GifPickerMenu: React.FC<{
  onPick: (id: string) => void;
}> = ({ onPick }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<GifResult[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [debounced, setDebounced] = useState('');
  const [attachingId, setAttachingId] = useState<string | null>(null);

  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(query.trim());
    }, 300);
    return () => {
      clearTimeout(t);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setNextOffset(null);
    dispatch(searchGifs(debounced, 0))
      .then((res: unknown) => {
        if (cancelled) return;
        const data = res as SearchResponse;
        setItems(data.items);
        setNextOffset(data.next_offset);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setNextOffset(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, dispatch]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current) return;
    if (nextOffset === null) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    dispatch(searchGifs(debounced, nextOffset))
      .then((res: unknown) => {
        const data = res as SearchResponse;
        setItems((prev) => {
          const seen = new Set(prev.map((it) => it.id));
          return [...prev, ...data.items.filter((it) => !seen.has(it.id))];
        });
        setNextOffset(data.next_offset);
      })
      .catch(() => {
        setNextOffset(null);
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [dispatch, debounced, nextOffset]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = gridRef.current;
    if (!node || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { root, rootMargin: '40px', threshold: 0.1 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [loadMore, items.length]);

  const handleSelect = useCallback(
    (id: string) => {
      if (attachingId) return;
      setAttachingId(id);
      dispatch(attachGif(id))
        .catch(() => {
          // upload reducer is unchanged on failure; nothing else to do here.
        })
        .finally(() => {
          setAttachingId(null);
          onPick(id);
        });
    },
    [dispatch, attachingId, onPick],
  );

  return (
    <div className='gif-picker__menu'>
      <input
        type='text'
        className='gif-picker__search'
        placeholder={intl.formatMessage(messages.search)}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
        autoFocus
      />
      <div className='gif-picker__grid' ref={gridRef}>
        {!loading && items.length === 0 && (
          <div className='gif-picker__empty'>
            {intl.formatMessage(messages.empty)}
          </div>
        )}
        {items.map((item) => (
          <button
            key={item.id}
            type='button'
            className={classNames('gif-picker__item', {
              'gif-picker__item--loading': attachingId === item.id,
            })}
            onClick={() => {
              handleSelect(item.id);
            }}
            disabled={!!attachingId}
            title={item.description}
          >
            <img src={item.preview.url} alt={item.description} loading='lazy' />
          </button>
        ))}
        {nextOffset !== null && (
          <div ref={sentinelRef} className='gif-picker__sentinel' aria-hidden>
            {loadingMore ? '…' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export const GifPickerDropdown: React.FC = () => {
  const intl = useIntl();
  const [active, setActive] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    setActive((v) => !v);
  }, []);

  const handleClose = useCallback(() => {
    setActive(false);
  }, []);

  const popperConfig = useMemo(
    () => ({
      strategy: 'fixed' as const,
      modifiers: [
        {
          name: 'preventOverflow',
          options: { boundary: 'viewport', padding: 8 },
        },
      ],
    }),
    [],
  );

  return (
    <div className={classNames('gif-picker', { 'gif-picker--active': active })}>
      <IconButton
        className='gif-picker__button'
        ref={buttonRef}
        title={intl.formatMessage(messages.open)}
        icon='gif'
        iconComponent={GifBoxIcon}
        onClick={handleToggle}
        active={active}
      />

      <Overlay
        show={active}
        offset={[0, 8]}
        placement='bottom-start'
        flip
        target={buttonRef as unknown as React.RefObject<HTMLElement>}
        rootClose
        onHide={handleClose}
        popperConfig={popperConfig}
      >
        {({ props }) => (
          <div
            {...props}
            className='gif-picker__popover'
            style={{ ...props.style }}
          >
            <GifPickerMenu onPick={handleClose} />
          </div>
        )}
      </Overlay>
    </div>
  );
};
